import React from 'react'
import clsx from 'clsx'
import { ChevronRight, Clock, AlertTriangle } from 'lucide-react'
import type { Step } from '../../types/session.types'
import { StepStatusBadge } from './StepStatusBadge'
import { useUpdateStepStatus } from '../../hooks/useStepMutations'
import { useCanUpdateStep } from '../../stores/selectors'
import styles from './StepItem.module.css'

export interface StepItemProps {
  step: Step
  index: number
  sessionId: string
  onClick: () => void
  className?: string
}

export function StepItem({ step, index, sessionId, onClick, className }: StepItemProps) {
  const canUpdate = useCanUpdateStep(step.team_id)
  const { mutate: updateStatus, isPending } = useUpdateStepStatus(sessionId)

  const isRunning = step.status === 'in_progress'
  const isReady = step.status === 'ready'
  const isBlocked = step.status === 'blocked'
  const isTerminal = step.status === 'done' || step.status === 'skipped'

  // Elapsed time calculation
  const elapsedMins = step.started_at
    ? Math.floor((Date.now() - new Date(step.started_at).getTime()) / 60_000)
    : 0
  const isOvertime = isRunning && elapsedMins > step.estimated_duration_mins

  return (
    <div
      className={clsx(
        styles.item,
        styles[`item_${step.status}`],
        isOvertime && styles.itemOvertime,
        className
      )}
    >
      {/* Left: Step number + info */}
      <div className={styles.left}>
        <span className={clsx(styles.number, isTerminal && styles.numberDone)}>
          {isTerminal ? '✓' : index}
        </span>
        <div className={styles.info}>
          <button
            type="button"
            className={styles.nameBtn}
            onClick={onClick}
            title="View step details"
          >
            <span className={styles.name}>{step.name}</span>
            <ChevronRight size={14} className={styles.arrow} />
          </button>

          {/* Running status */}
          {isRunning && (
            <div className={styles.runningInfo}>
              <Clock size={12} />
              <span>
                {elapsedMins}m / est {step.estimated_duration_mins}m
              </span>
              {isOvertime && (
                <span className={styles.overtimeTag}>
                  <AlertTriangle size={11} />
                  +{elapsedMins - step.estimated_duration_mins}m overtime
                </span>
              )}
            </div>
          )}

          {/* Blocked info */}
          {isBlocked && (
            <span className={styles.blockedInfo}>
              ⏳ Waiting for {step.dependencies.length} step(s)
            </span>
          )}

          {/* Done info */}
          {isTerminal && step.executed_by && (
            <span className={styles.doneInfo}>
              By {step.executed_by}
              {step.actual_duration_mins && ` · ${Math.round(step.actual_duration_mins)}m`}
            </span>
          )}
        </div>
      </div>

      {/* Right: status badge + actions */}
      <div className={styles.right}>
        <StepStatusBadge status={step.status} />

        {canUpdate && (
          <div className={styles.actions}>
            {isReady && (
              <button
                type="button"
                id={`step-start-${step.id}`}
                className={clsx(styles.actionBtn, styles.actionBtnStart)}
                onClick={() => updateStatus({ stepId: step.id, status: 'in_progress' })}
                disabled={isPending}
              >
                ▶ Start
              </button>
            )}
            {isRunning && (
              <>
                <button
                  type="button"
                  id={`step-done-${step.id}`}
                  className={clsx(styles.actionBtn, styles.actionBtnDone)}
                  onClick={() => updateStatus({ stepId: step.id, status: 'done' })}
                  disabled={isPending}
                >
                  ✓ Done
                </button>
                <button
                  type="button"
                  id={`step-fail-${step.id}`}
                  className={clsx(styles.actionBtn, styles.actionBtnFail)}
                  onClick={() => updateStatus({ stepId: step.id, status: 'failed' })}
                  disabled={isPending}
                >
                  ✗ Failed
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
